import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { phaseStyle } from "@/lib/project_phases";

/**
 * GlobalSearchModal · buscador ⌘K (C2).
 *
 * Overlay con caja de búsqueda y resultados agrupados por tipo. Backend:
 * `GET /api/search?q=`. Debounce 220ms.
 *
 * Navegación con teclado: ↑/↓ recorre resultados, Enter selecciona, Esc cierra.
 *
 * Se monta en AppShell y ProjectShell — ambos lo abren al pulsar ⌘K
 * (controlado desde el contexto del shell o como singleton via key prop).
 */

export default function GlobalSearchModal({ open, onClose }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ projects: [], orgs: [], calls: [], scenes: [] });
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const nav = useNavigate();

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setData({ projects: [], orgs: [], calls: [], scenes: [] }); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setData(r.data || { projects: [], orgs: [], calls: [], scenes: [] });
        setSel(0);
      } catch (_e) { /* ignore */ }
      setLoading(false);
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, open]);

  // Focus al abrir
  useEffect(() => {
    if (open) {
      setQ("");
      setData({ projects: [], orgs: [], calls: [], scenes: [] });
      setSel(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Lista plana de resultados (para navegación con flechas)
  const flat = [
    ...data.projects.map((p) => ({ kind: "project", id: p.id, item: p })),
    ...data.orgs.map((o) => ({ kind: "org", id: o.id, item: o })),
    ...data.calls.map((c) => ({ kind: "call", id: c.id, item: c })),
    ...data.scenes.map((s) => ({ kind: "scene", id: `${s.project_id}-${s.id}`, item: s })),
  ];

  const navigate = (entry) => {
    if (entry.kind === "project") nav(`/project/${entry.item.id}`);
    else if (entry.kind === "scene") nav(`/project/${entry.item.project_id}?tab=screenplay`);
    else if (entry.kind === "org") nav("/funding");
    else if (entry.kind === "call") nav("/funding");
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") onClose();
    else if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(flat.length - 1, s + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === "Enter" && flat[sel]) { e.preventDefault(); navigate(flat[sel]); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-gb-black/70 px-4 pt-20 backdrop-blur"
         onClick={onClose} data-testid="global-search-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-gb-paper px-5 py-4">
          <span className="text-xl text-gb-muted">⌕</span>
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={onKeyDown} placeholder="Buscar proyectos, organizaciones, escenas, convocatorias…"
                 data-testid="global-search-input"
                 className="flex-1 bg-transparent text-base outline-none placeholder:text-gb-muted/60" />
          <kbd className="hidden rounded border border-gb-paper px-1.5 py-0.5 font-mono text-[10px] text-gb-muted sm:inline">esc</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
          {q.length < 2 && (
            <p className="px-5 py-10 text-center text-sm text-gb-muted">
              Escribe al menos 2 caracteres. Usa <kbd className="rounded border border-gb-paper px-1 font-mono text-[10px]">↑</kbd> <kbd className="rounded border border-gb-paper px-1 font-mono text-[10px]">↓</kbd> y <kbd className="rounded border border-gb-paper px-1 font-mono text-[10px]">Enter</kbd> para navegar.
            </p>
          )}
          {q.length >= 2 && loading && (
            <p className="px-5 py-10 text-center text-sm text-gb-muted">Buscando…</p>
          )}
          {q.length >= 2 && !loading && flat.length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-gb-muted" data-testid="global-search-empty">
              Sin resultados para "{q}".
            </p>
          )}

          {flat.length > 0 && (() => {
            let cursor = 0;
            return (
              <>
                <Group label="Proyectos" items={data.projects}
                       render={(p, idx) => {
                         const i = cursor + idx;
                         const sty = phaseStyle(p.phase);
                         return (
                           <Row key={p.id} active={i === sel} onClick={() => navigate(flat[i])}
                                testid={`search-result-project-${p.id}`}>
                             <span className="inline-block h-2 w-2 rounded-full" style={{ background: sty.hex }} />
                             <div className="min-w-0 flex-1">
                               <p className="truncate font-medium">{p.title}</p>
                               <p className="text-[11px] text-gb-muted">
                                 {p.project_type}{p.director && ` · ${p.director}`}
                               </p>
                             </div>
                             <span className="text-[10px] uppercase tracking-widest text-gb-muted">{sty.label}</span>
                           </Row>
                         );
                       }} />
                {(() => { cursor += data.projects.length; return null; })()}
                <Group label="Organizaciones" items={data.orgs}
                       render={(o, idx) => {
                         const i = cursor + idx;
                         return (
                           <Row key={o.id} active={i === sel} onClick={() => navigate(flat[i])}
                                testid={`search-result-org-${o.id}`}>
                             <span className="h-2 w-2 rounded-full bg-gb-red" />
                             <div className="min-w-0 flex-1">
                               <p className="truncate font-medium">{o.name}</p>
                               <p className="text-[11px] text-gb-muted">
                                 {o.org_type}{o.country && ` · ${o.country}`}
                               </p>
                             </div>
                           </Row>
                         );
                       }} />
                {(() => { cursor += data.orgs.length; return null; })()}
                <Group label="Convocatorias" items={data.calls}
                       render={(c, idx) => {
                         const i = cursor + idx;
                         return (
                           <Row key={c.id} active={i === sel} onClick={() => navigate(flat[i])}
                                testid={`search-result-call-${c.id}`}>
                             <span className="h-2 w-2 rounded-full bg-amber-500" />
                             <div className="min-w-0 flex-1">
                               <p className="truncate font-medium">{c.title}</p>
                               <p className="text-[11px] text-gb-muted">
                                 {c.organism}{c.closes_at && ` · cierra ${c.closes_at}`}
                               </p>
                             </div>
                           </Row>
                         );
                       }} />
                {(() => { cursor += data.calls.length; return null; })()}
                <Group label="Escenas del guion" items={data.scenes}
                       render={(s, idx) => {
                         const i = cursor + idx;
                         return (
                           <Row key={`${s.project_id}-${s.id}`} active={i === sel}
                                onClick={() => navigate(flat[i])}
                                testid={`search-result-scene-${s.id}`}>
                             <span className="h-2 w-2 rounded-full bg-sky-600" />
                             <div className="min-w-0 flex-1">
                               <p className="truncate font-medium">{s.scene_heading}</p>
                               <p className="text-[11px] text-gb-muted">{s.project_title}</p>
                             </div>
                           </Row>
                         );
                       }} />
              </>
            );
          })()}
        </div>

        {/* Hint footer */}
        {flat.length > 0 && (
          <div className="border-t border-gb-paper bg-gb-paper-soft/50 px-5 py-2 text-[10px] uppercase tracking-widest text-gb-muted">
            <span>{flat.length} resultado{flat.length === 1 ? "" : "s"}</span>
            <span className="ml-3">↑↓ navegar · Enter abrir · Esc cerrar</span>
          </div>
        )}
      </div>
    </div>
  );
}

const Group = ({ label, items, render }) => {
  if (!items?.length) return null;
  return (
    <div>
      <div className="border-y border-gb-paper bg-gb-paper-soft/40 px-5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.25em] text-gb-muted">
        {label} <span className="ml-1 tabular-nums">{items.length}</span>
      </div>
      <ul>{items.map((it, idx) => render(it, idx))}</ul>
    </div>
  );
};

const Row = ({ active, onClick, children, testid }) => (
  <li>
    <button onClick={onClick} data-testid={testid}
            className={`flex w-full items-center gap-3 px-5 py-2.5 text-left text-sm transition ${
              active ? "bg-gb-red/8 text-gb-red" : "hover:bg-gb-paper-soft/60"
            }`}>
      {children}
    </button>
  </li>
);
